import React, { useEffect, useRef, useCallback } from 'react';
import { onValue, ref } from "firebase/database";
import { db } from "../../config/firebase";
import moment from "moment-timezone";
import _ from 'lodash';

const FirebaseDeviceListener = ({ path, setRealtimeData }) => {
    const prevDataRef = useRef(null);
    const currentPathRef = useRef(path);

    // Memoize the data transformation function
    const transformData = useCallback((data) => {
        const now = moment();
        const currentMediaId = data.currentMedia?.id;
        const cornerMediaId = data.corner?.id;
        const bannerMediaId = data.banner?.id;
        const playgroundMediaId = data.playground?.id;

        const schedule_id = data?.playingNow?.current_scheduled_id ? data.playingNow.current_scheduled_id : "";

        const mediaPlayingEntry = currentMediaId
            ? {
                currentMediaId,
                index: data.currentMedia?.index,
                start_time: moment(data.currentMedia?.start_time).local(),
                ...(data.currentMedia?.pause_start_time && {
                    pause_start_time: moment(data.currentMedia.pause_start_time).local(),
                }),
            } : {};

        const cornerPlayingEntry = cornerMediaId
            ? {
                id: cornerMediaId,
                index: data.corner?.index || 0,
                start_time: moment(data.corner?.start_time).local(),
            } : {};


        const bannerPlayingEntry = bannerMediaId
            ? {
                id: bannerMediaId,
                index: data.banner?.index || 0,
                start_time: moment(data.banner?.start_time).local(),
            } : {};


        const playgroundPlayingEntry = playgroundMediaId
            ? {
                id: playgroundMediaId,
                index: data.playground?.index || 0,
                start_time: moment(data.playground?.start_time).local(),
            } : {};

        return {
            id: data?.playingNow?.current_campaign_id ? data.playingNow.current_campaign_id : "",
            name: data?.playingNow?.current_campaign ? data.playingNow.current_campaign : "",
            schedule_id: schedule_id,
            tag: data.campaignType,
            is_playing: data.isPlaying,
            screen_angle: data.screenAngle,
            is_muted: data.isMuted,
            download_percentage: data.DownloadingPercentage,
            first_login: moment.unix((data?.firstLogin ?? now.valueOf()) / 1000).local(),
            media_playing: mediaPlayingEntry,
            app_version: data.app_version,
            corner: cornerPlayingEntry,
            banner: bannerPlayingEntry,
            playground: playgroundPlayingEntry,
            screenShotTime: data.screenshotTimestamp
        };
    }, []);

    // Stable callback for setting data
    const handleDataUpdate = useCallback((newData) => {
        if (!_.isEqual(newData, prevDataRef.current)) {
            prevDataRef.current = newData;
            setRealtimeData(newData);
        }
    }, [setRealtimeData]);

    useEffect(() => {
        // Only recreate listener if path actually changes
        if (currentPathRef.current !== path) {
            currentPathRef.current = path;
        }

        const fullPath = `${process.env.REACT_APP_APP_ENV}/${currentPathRef.current}`;
        const query = ref(db, fullPath);

        const unsubscribe = onValue(query, (snapshot) => {
            if (!snapshot.exists()) return;

            const transformedData = transformData(snapshot.val());
            handleDataUpdate(transformedData);
        });

        return () => {
            unsubscribe();
        };
    }, [path, transformData, handleDataUpdate]);

    return null;
};

export default React.memo(FirebaseDeviceListener);
